import React from 'react';
import { PageHeader, Section } from '../components/PageShell';
import { Button } from '../components/Button';
import {
  Factory, HeartPulse, HardHat, Building2, ShoppingBag,
  Home as HomeIcon, Utensils, GraduationCap, Landmark, ShieldCheck, Truck
} from 'lucide-react';

const industries = [
  { icon: Factory,       name: 'Manufacturing',        desc: 'MES, digital twins, predictive maintenance and shop-floor AI at scale.', tags: ['IIoT', 'MES', 'Predictive'] },
  { icon: HeartPulse,    name: 'Healthcare',           desc: 'HIPAA-ready patient platforms, clinical copilots and EHR interoperability.', tags: ['HIPAA', 'FHIR', 'Clinical AI'] },
  { icon: HardHat,       name: 'Construction',         desc: 'Digital permits, safety intelligence, project controls and site ops.', tags: ['PTW', 'Safety AI', 'Ops'] },
  { icon: Building2,     name: 'Facility Management',  desc: 'Assets, PPM, visitor, complaint and permit systems in a unified platform.', tags: ['CMMS', 'VMS', 'ERP'] },
  { icon: ShoppingBag,   name: 'Retail',               desc: 'Composable commerce, personalisation and OMS built for global scale.', tags: ['Headless', 'OMS', 'AI'] },
  { icon: HomeIcon,      name: 'Real Estate',          desc: 'Deal platforms, portfolio analytics and tenant-facing digital experiences.', tags: ['CRE', 'Portfolio', 'Tenant'] },
  { icon: Utensils,      name: 'Hospitality',          desc: 'PMS, guest apps and revenue-management AI for hotels, F&B and venues.', tags: ['PMS', 'Guest App', 'RevMgmt'] },
  { icon: GraduationCap, name: 'Education',            desc: 'Learning platforms, adaptive tutoring and integrity-preserving AI.', tags: ['LMS', 'Adaptive AI', 'Assessment'] },
  { icon: Landmark,      name: 'Finance',              desc: 'Core banking modernisation, RegTech, payments and wealth platforms.', tags: ['Core', 'RegTech', 'Payments'] },
  { icon: ShieldCheck,   name: 'Government',           desc: 'Citizen portals, digital services and interoperable data platforms.', tags: ['Citizen', 'GovTech', 'Interop'] },
  { icon: Truck,         name: 'Logistics',            desc: 'Real-time visibility, route optimisation and AI-driven planning.', tags: ['TMS', 'Optimisation', 'ETA AI'] },
];

export default function Industries() {
  return (
    <div data-testid="industries-page" className="pb-24">
      <PageHeader
        eyebrow="Industries"
        title={<>We ship into <span className="font-serif">regulated,</span> serious environments.</>}
        subtitle="Enterprise software done well means understanding the constraints &mdash; regulatory, operational and organisational. Here&rsquo;s where we do it best."
      >
        <div className="flex flex-wrap gap-3">
          <Button to="/contact" variant="primary">Talk about your industry</Button>
          <Button to="/case-studies" variant="secondary">View Case Studies</Button>
        </div>
      </PageHeader>

      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {industries.map(({ icon: Icon, name, desc, tags }, i) => (
            <div
              key={i}
              data-testid={`industry-card-${i}`}
              className="rounded-[28px] bg-white border border-[#7C3AED]/12 p-7 shadow-[0_4px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_40px_rgba(109,40,217,0.18)] hover:border-[#7C3AED]/35 hover:-translate-y-1 transition-all duration-300 flex flex-col"
            >
              <div className="w-11 h-11 rounded-2xl grad-icon text-white flex items-center justify-center shadow-[0_6px_20px_rgba(109,40,217,0.28)] mb-5">
                <Icon className="w-4 h-4" />
              </div>
              <h3 className="font-serif text-xl text-[#0D212C] mb-2">{name}</h3>
              <p className="text-sm text-[#273C46] leading-relaxed mb-5">{desc}</p>
              <div className="mt-auto flex flex-wrap gap-2">
                {tags.map((t) => (
                  <span key={t} className="text-[11px] px-2.5 py-1 rounded-full bg-[#7C3AED]/8 text-[#4C1D95] font-mono">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
